import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { fetchItemFamilies, selectItemFamilies } from './itemFamiliesSlice';
import { selectItemFamilyFilters } from '../app/appSlice';
import { selectCurrentItemOwner } from '../itemOwner/itemOwnersSlice';
import { selectCountriesWithNames } from '../countries/countriesSlice';

import useTitle from '../../hooks/use-title.hook';

import {
  makeStyles,
  Button,
  Typography,
  Grid,
  Box
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';

import TopSection from '../common/topSection';
import ItemFamilyGrid from './ItemFamilyGrid';
import { OutlinedTextField, Select } from '../common/fields';
import Loading from '../common/loading';
import Alert from '../common/alert';

const useStyles = makeStyles((theme) => ({
  newBtn: {
    marginLeft: 'auto'
  },
}));

const ItemFamilies = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const itemOwnerId = useSelector(selectCurrentItemOwner);
  const { itemFamilies, status, error } = useSelector(selectItemFamilies);
  console.log(itemFamilies)
  const { vehicleTypes, seasons, brands } = useSelector(selectItemFamilyFilters);
  const markets = useSelector(selectCountriesWithNames);
  const [ filters, setFilters ] = useState({vehicleType: '', season: '', brand: ''});

  const booleanOptions = [{name: t('common:yes'), value: true}, {name: t('common:no'), value: false}];

  useTitle(t('item:itemFamilies'));

  useEffect(() => {
    // Fetch all from server
    if(status === 'idle') {
      dispatch(fetchItemFamilies({ownerId: itemOwnerId}));
    }
  }, [dispatch, itemOwnerId, status]);

  const itemFamiliesWithRouterLinks = itemFamilies.map(item => ({
    ...item,
    link: `/pih/owner/${itemOwnerId}/families/${item.code}`
  })).filter(item => {

    const matches = {
      vehicleType: true,
      season: true,
      brand: true,
      name: true,
      outOfProduction: true,
      market: true,
    };

    if(filters.vehicleType) {
      matches.vehicleType = false;

      if(itemOwnerId === 'PC1') {
        if(Array.isArray(item.itemLines)) {
          const allVehicleTypesPerItem = item.itemLines.map(row => row.vehicleType);
          matches.vehicleType = allVehicleTypesPerItem.includes(filters.vehicleType);
        }
      } else {
        matches.vehicleType = item.usageGroup.includes(filters.vehicleType);
      }
    }

    if(filters.season) {
      matches.season = false;

      if(itemOwnerId === 'PC1') {
        if(Array.isArray(item.itemLines)) {
          const allSeasonsPerItem = item.itemLines.map(row => row.season);
          matches.season = allSeasonsPerItem.includes(filters.season);
        }
      } else {
        matches.season = item.season === filters.season;
      }
    }

    if(filters.brand) {
      matches.brand = item.brand === filters.brand;
    }

    if(filters.name) {
      matches.name = item.name.toLowerCase().indexOf(filters.name.toLowerCase()) > -1;
    }

    if(filters.outOfProduction === true) {
      matches.outOfProduction = item.attributes.includes('out-of-production');
    }

    if(filters.outOfProduction === false) {
      matches.outOfProduction = item.attributes.includes('out-of-production') === false;
    }

    if(filters.market) {
      matches.market = item.markets.includes(filters.market);
    }

    return Object.values(matches).every(val => val === true);
  });

  const handleFilterChange = event => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value
    });
  }

  return (
    <>
      <TopSection>
        <Typography variant="h4" component="h1">{t('item:itemFamilies')}</Typography>
        <Button
          component={Link}
          to={`/pih/owner/${itemOwnerId}/families/new`}
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<AddIcon />}
          className={classes.newBtn}
        >
          {t('itemFamily:addNew')}
        </Button>
      </TopSection>

      <Box p={3.5}>
        <Box mb={5}>
          <Grid container spacing={5}>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                label={t('itemFamily:vehicleType')}
                name="vehicleType"
                value={filters.vehicleType}
                options={vehicleTypes.map(item => ({label: item.name, value: item.code}))}
                onChange={handleFilterChange}
                disabled={false}
                showEmptyOption
                emptyOptionLabel={t('common:showAll')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                label={t('itemFamily:season')}
                name="season"
                value={filters.season}
                options={seasons.map(item => ({label: item.name, value: item.code}))}
                onChange={handleFilterChange}
                disabled={false}
                showEmptyOption
                emptyOptionLabel={t('common:showAll')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                label={t('itemFamily:brand')}
                name="brand"
                value={filters.brand}
                options={brands.map(item => ({label: item.name, value: item.code}))}
                onChange={handleFilterChange}
                disabled={false}
                showEmptyOption
                emptyOptionLabel={t('common:showAll')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                label={t('itemFamily:outOfProduction')}
                name="outOfProduction"
                value={filters.outOfProduction}
                options={booleanOptions.map(item => ({label: item.name, value: item.value}))}
                onChange={handleFilterChange}
                disabled={false}
                showEmptyOption
                emptyOptionLabel={t('common:showAll')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                label={t('itemFamily:availableInMarket')}
                name="market"
                value={filters.market}
                options={markets.map(item => ({label: item.name, value: item.id}))}
                onChange={handleFilterChange}
                disabled={false}
                showEmptyOption
                emptyOptionLabel={t('common:showAll')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <OutlinedTextField
                label={t('itemFamily:name')}
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
                disabled={false}
              />
            </Grid>
          </Grid>

        </Box>

        {status === 'loading' ? <Loading /> : null}
        {status === 'failed' ? <Alert severity="error">{t(`errors:${error.message}`)}</Alert> : null}
        {status === 'succeeded' ? <ItemFamilyGrid items={itemFamiliesWithRouterLinks} /> : null}
      </Box>
    </>
  );
}

export default ItemFamilies;
